import React, { useContext, useEffect, useState, useRef } from 'react'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import { ETLProvider, useETLContext } from 'containers/etl/ETLContext'
import ETLProgressCard from './ETLProgressCard'
import ETLHistoryTimeline from './ETLHistoryTimeline'
import ScreenLoading from 'components/ScreenLoading'
import TagManager from 'react-gtm-module'

import './DataLoad.scss'

const DataLoadContent = ({ embedded = false }: { embedded?: boolean }) => {
  const {
    liveProgress,
    wsConnected,
    currentRuns,
    activeRunCount,
    etlLogs,
    loadingLogs,
    errorGettingLogs,
    getETLlogs,
    runSteps,
    runStepsLoading,
    getRunSteps,
    triggerETL,
    triggerLoading,
    refreshCurrentRun,
    hasMoreLogs,
    loadMoreLogs,
    loadingMoreLogs,
  } = useETLContext()

  // Resize state
  const [progressHeight, setProgressHeight] = useState(40) // percentage
  const [isResizing, setIsResizing] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Refresh current run on mount
    refreshCurrentRun()
  }, [])

  useEffect(() => {
    if (isResizing) {
      const updateHeight = (clientY: number) => {
        if (!containerRef.current) return

        const containerRect = containerRef.current.getBoundingClientRect()
        const containerHeight = containerRect.height
        const posY = clientY - containerRect.top

        // Calculate percentage (with min/max constraints - extended range)
        let newPercentage = (posY / containerHeight) * 100
        newPercentage = Math.max(5, Math.min(95, newPercentage))

        setProgressHeight(newPercentage)
      }

      const handleMouseMove = (e: MouseEvent) => {
        e.preventDefault()
        updateHeight(e.clientY)
      }

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault()
        if (e.touches.length > 0) {
          updateHeight(e.touches[0].clientY)
        }
      }

      const handleEnd = () => {
        setIsResizing(false)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleEnd)
      document.addEventListener('touchmove', handleTouchMove, { passive: false })
      document.addEventListener('touchend', handleEnd)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleEnd)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleEnd)
      }
    }
  }, [isResizing])

  const handleTriggerETL = async () => {
    TagManager.dataLayer({
      dataLayer: { event: 'data_load_button_clicked', is_custom_track: true },
    })
    await triggerETL()
  }

  const handleRefreshHistory = () => {
    getETLlogs()
  }

  const handleLoadRunSteps = (runId: string) => {
    getRunSteps(runId)
  }

  if (loadingLogs && !etlLogs) {
    return <ScreenLoading />
  }

  return (
    <div className="data-load-page">
      <div className="data-load-header">
        {!embedded && <h2>ETL</h2>}
        <p className="subtitle-text">
          Monitor real-time sync progress across all your connected sources and
          view historical runs
        </p>
      </div>

      <div className="resizable-container" ref={containerRef}>
        <div
          className="progress-section-wrapper"
          style={{ height: `${progressHeight}%` }}
        >
          <ETLProgressCard
            liveProgress={liveProgress}
            currentRuns={currentRuns}
            activeRunCount={activeRunCount}
            wsConnected={wsConnected}
            onTriggerETL={handleTriggerETL}
            triggerLoading={triggerLoading}
          />
        </div>

        <div
          className={`resize-handle ${isResizing ? 'resizing' : ''}`}
          onMouseDown={() => setIsResizing(true)}
          onTouchStart={() => setIsResizing(true)}
        >
          <div className="resize-handle-line" />
          <span className="material-icons resize-icon">drag_handle</span>
        </div>

        <div
          className="history-section-wrapper"
          style={{ height: `${100 - progressHeight}%` }}
        >
          {errorGettingLogs ? (
            <div className="error-message">
              <span className="material-icons">error</span>
              <span>{errorGettingLogs}</span>
            </div>
          ) : (
            <ETLHistoryTimeline
              allLogs={etlLogs}
              onRefresh={handleRefreshHistory}
              onLoadRunSteps={handleLoadRunSteps}
              runSteps={runSteps}
              runStepsLoading={runStepsLoading}
              hasMore={hasMoreLogs}
              onLoadMore={loadMoreLogs}
              loadingMore={loadingMoreLogs}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// `embedded`: rendered as the Data Sync tab of the Data & Insights flow — the shell
// provides the tab label, so the page's own "ETL" heading hides.
const DataLoad = ({ embedded = false }: { embedded?: boolean }) => {
  const { currentOrganization } = useContext(OrganizationsContext)

  if (!currentOrganization.id) {
    return <ScreenLoading />
  }

  return (
    <ETLProvider organization={currentOrganization} enableWebSocket={true}>
      <DataLoadContent embedded={embedded} />
    </ETLProvider>
  )
}

export default React.memo(DataLoad)
